import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import { buttonVariants, cn } from '@/lib/utils';
import { type ButtonIconType } from '@/lib/types';
import { ConfigContext } from '@/config/config-context';

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	icon?: ButtonIconType;
	floating?: boolean;
	focusButton?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, variant, size, icon, floating, asChild = false, ...props },
		ref
	) => {
		const config = React.useContext(ConfigContext);

		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={
					cn(
						buttonVariants({ variant, size }) +
							' wlcb-flex wlcb-items-center wlcb-justify-center wlcb-gap-x-2',
						{
							'wlcb-flex-row-reverse ': icon?.orientation === 'left',
							'!wlcb-p-0 wlcb-size-8 wlcb-min-w-8 wlcb-min-h-8':
								!props.name && size === 'sm',
							'!wlcb-p-0 wlcb-size-10 wlcb-min-w-10 wlcb-min-h-10':
								!props.name && size === 'md',
							'!wlcb-p-0 wlcb-size-12 wlcb-min-w-12 wlcb-min-h-12':
								!props.name && size === 'lg',
							'!wlcb-p-0 wlcb-size-16 wlcb-min-w-16 wlcb-min-h-16':
								!props.name && size === 'xl',
							'wlcb-shadow-md': floating,
							// Primary -- Theme
							'wlcb-bg-theme-brand-primary hover:wlcb-bg-theme-brand-primary-hover active:wlcb-bg-theme-brand-primary-pressed wlcb-text-theme-brand-primary-text-color':
								config?.theme?.primary !== undefined && variant === 'primary',
							// Ghost -- Theme
							'hover:wlcb-bg-theme-brand-primary-hover wlcb-text-theme-brand-primary-text-color':
								config?.theme?.primary?.color !== undefined &&
								variant === 'ghost',
							// Secondary -- Theme
							'wlcb-bg-theme-brand-secondary hover:wlcb-bg-theme-brand-secondary-hover active:wlcb-bg-theme-brand-secondary-pressed wlcb-text-theme-brand-secondary-text-color':
								config?.theme?.secondary?.color !== undefined &&
								variant === 'secondary',
							// Outline -- Theme
							'wlcb-border-theme-brand-primary hover:wlcb-border-theme-brand-primary-hover wlcb-text-theme-brand-primary-text-color active:wlcb-border-theme-brand-pressed-primary':
								config?.theme?.primary?.color !== undefined &&
								variant === 'outline',
						}
					) + ` ${className ? className : ''}`
				}
				ref={ref}
				{...props}
			>
				{props.name ?? null} {icon ? icon.iconType : null}
			</Comp>
		);
	}
);
Button.displayName = 'Button';

export { Button };
